"use strict"

import React from "react"
import { Form } from "react-bootstrap"

import { groupBy } from "../../../resources/utils"
import { isLTPM, injectIntl } from "../../../../../components/shared_components_registration"
import SingleMultiTagSelectionRadio from "./SingleMultiTagSelectionRadio"
import OptionLabel from "./OptionLabel"
import { getOptionCSSClass, filterOptions, toggleExpandedCategory } from "./utils"
import { equalArrays } from "../../../../utils"

// Where we set distinctions for LTPM/LL versions of the component
const registration = {
  search_text: (intl) =>
    isLTPM ? intl.formatMessage({ id: "general.search", defaultMessage: "Search" }) : "Search",
  get_category_label: (intl, cat) =>
    isLTPM
      ? intl.formatMessage({ id: `general.${cat.toLowerCase().split(" ").join(".")}`, defaultMessage: cat })
      : cat,
  getTagDetailLabel: (intl) => "Show Tag Logging Details", // TODO - Get translations
}

const siteReactorFilter = (uniqueSites, uniqueReactors, option) => {
  let extraInfo = option?.["dcs_logging_info"] || {}
  let filteredInfo = {}
  if (uniqueSites.length > 0 && uniqueReactors.length > 0) {
    /*
      {
        "{site_name}": {
          "Fermentation Loop": {
            "details": ["ABC123, ABC456",...],
            "reactors": ["T1R1", ...]
          },
          ...
          ...
        },
        "{site_name}": {...},
        ...
      }
    */
    Object.entries(extraInfo).forEach(([site, siteInfo]) => {
      if (uniqueSites.includes(site)) {
        const finalSiteInfo = {}
        let hadMatch = false
        Object.entries(siteInfo).forEach(([sys, sysInfo]) => {
          if (uniqueReactors.filter((r) => (sysInfo?.reactors || []).includes(r)).length > 0) {
            finalSiteInfo[sys] = sysInfo
            hadMatch = true
          }
        })
        if (hadMatch) {
          filteredInfo[site] = finalSiteInfo
        }
      }
    })
  } else {
    filteredInfo = extraInfo
  }

  const finalInfo = {}
  Object.entries(filteredInfo).forEach(([site, siteInfo]) => {
    Object.entries(siteInfo).forEach(([sys, sysInfo]) => {
      finalInfo[`${site} ${sys}`] = sysInfo?.details || []
    })
  })

  return {
    ...option,
    dcs_logging_info: finalInfo,
  }
}

class _CategorizedSelector extends React.Component {
  static defaultProps = {
    categoryFn: (x) => x.category,
    nameFn: (x) => x.name,
  }

  constructor(props) {
    super(props)

    this.state = {
      expanded: null,
      collapse: true,
      searchString: "",
      tagSelectionMethod: "single",
      selected_tags: {},
      showLoggingDetails: true,
    }
  }

  componentDidMount() {
    if (this.props.options && [...new Set(this.props.options.map((opt) => opt.category))].length === 1) {
      this.setState({
        collapse: false,
        expanded: this.props.options[0].category,
      })
    }
  }

  render() {
    const {
      categories = [],
      options,
      categoryFn,
      nameFn,
      onSelect,
      onMultiTagSubmit,
      runFilters = [],
      intl,
    } = this.props
    const { searchString, selected_tags, tagSelectionMethod, collapse, showLoggingDetails } = this.state

    const uniqueSites = [...new Set(runFilters.map((f) => f.site))].filter((s) => s)
    const uniqueReactors = [...new Set(runFilters.map((f) => f.reactor))].filter((r) => r)

    let finalOptions = options
    if (!isLTPM) {
      finalOptions = options.map((o) => siteReactorFilter(uniqueSites, uniqueReactors, o))
    }

    const groupedOptions = groupBy(finalOptions, categoryFn),
      groupedFilteredOptions = groupBy(
        filterOptions(finalOptions, nameFn, searchString, uniqueSites, uniqueReactors),
        categoryFn,
      )

    const allCategories = [...categories]
    for (const cat of Object.keys(groupedOptions)) {
      if (allCategories.indexOf(cat) < 0) {
        allCategories.push(cat)
      }
    }

    let expanded = this.state.expanded || allCategories[0]
    if (!groupedFilteredOptions[expanded]) {
      expanded = Object.keys(groupedFilteredOptions)[0]
    }

    const updateTagSelectionMethod = (value) => {
      this.setState({
        tagSelectionMethod: value,
        selected_tags: {},
      })
    }

    const onFormSubmit = (e, onSubmit) => {
      e.preventDefault()
      this.props.onHide()
      onSubmit()
    }

    const getTagKey = (option) => {
      return (option.observations || []).length > 0 && !option.notAvailable
        ? `${option.observations[0].tag}${
            option.observations[0].smp_src ? `_${option.observations[0].smp_src}` : ""
          }`
        : null
    }

    return (
      <div className="cs-box" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div style={{ padding: "5px" }}>
          <div>
            {onMultiTagSubmit && (
              <SingleMultiTagSelectionRadio
                intl={intl}
                currentSelection={tagSelectionMethod}
                updateTagSelect={updateTagSelectionMethod}
                disabled={Object.keys(selected_tags).length === 0}
                onMultiTagSubmit={(e) =>
                  onFormSubmit(e, () => onMultiTagSubmit(Object.values(selected_tags)))
                }
              />
            )}
          </div>
          {!equalArrays(allCategories, ["Run Parameters", "Run Metadata"]) && (
            <div>
              <div className={"form-check form-switch"} style={{ paddingTop: "2px" }}>
                <input
                  type={"checkbox"}
                  id={"tag_logging_toggle"}
                  className={"form-check-input"}
                  checked={showLoggingDetails}
                  onChange={() => this.setState({ showLoggingDetails: !showLoggingDetails })}
                />
                <label
                  htmlFor={"tag_logging_toggle"}
                  className={"form-check-label"}
                  style={{ fontSize: "90%" }}
                >
                  {registration.getTagDetailLabel(intl)}
                </label>
              </div>
            </div>
          )}
        </div>

        <div>
          <input
            type="text"
            style={{ width: "100%" }}
            placeholder={`${registration.search_text(intl)}...`}
            value={searchString}
            onChange={(ev) =>
              this.setState({
                searchString: ev.target.value,
              })
            }
          />
        </div>
        <div style={{ overflowX: "hidden", overflowY: "scroll" }}>
          {allCategories.map((cat) => {
            const items = groupedOptions[cat],
              filteredItems = groupedFilteredOptions[cat] || []
            if (filteredItems.length === 0) {
              return <div key={cat}></div>
            }
            let content = null
            if (!collapse) {
              content = (
                <div
                  style={{
                    height: cat === expanded ? "auto" : "0px",
                    cursor: "pointer",
                    zIndex: "800",
                    overflow: "hidden",
                  }}
                >
                  {!onMultiTagSubmit || tagSelectionMethod === "single" ? (
                    <>
                      {(filteredItems || []).map((option, idx) => (
                        <div key={idx}>
                          <div
                            className={`cs-item ${getOptionCSSClass(option)}`}
                            onClick={(e) => onFormSubmit(e, () => onSelect(option))}
                          >
                            <OptionLabel
                              option={option}
                              intl={intl}
                              nameFn={nameFn}
                              showLoggingDetails={showLoggingDetails}
                            />
                          </div>
                          <hr style={{ marginTop: "0", marginBottom: "0" }} />
                        </div>
                      ))}
                    </>
                  ) : (
                    <>
                      {(filteredItems || []).map((option, idx) => {
                        const tag_name = getTagKey(option)
                        const is_checked = tag_name && Object.keys(selected_tags).includes(tag_name)
                        return (
                          <div key={idx}>
                            <div className={`cs-item ${getOptionCSSClass(option)}`}>
                              <div className={"row"} style={{ margin: "0" }}>
                                <div className={"col-2"} style={{ padding: "0" }}>
                                  <Form.Check
                                    type={"checkbox"}
                                    disabled={!tag_name}
                                    checked={is_checked}
                                    onChange={(e) => {
                                      let tags = { ...selected_tags }
                                      if (e.target.checked) {
                                        this.setState({
                                          selected_tags: {
                                            ...tags,
                                            [tag_name]: option,
                                          },
                                        })
                                      } else {
                                        delete tags[tag_name]
                                        this.setState({
                                          selected_tags: tags,
                                        })
                                      }
                                    }}
                                    id={`tag-sel-checkbox-${idx}`}
                                  />
                                </div>
                                <div className={"col-10"} style={{ padding: "0" }}>
                                  <OptionLabel
                                    option={option}
                                    intl={intl}
                                    nameFn={nameFn}
                                    showLoggingDetails={showLoggingDetails}
                                  />
                                </div>
                              </div>
                            </div>
                            <hr style={{ marginTop: "0", marginBottom: "0" }} />
                          </div>
                        )
                      })}
                    </>
                  )}
                </div>
              )
            }
            return (
              <div key={cat} style={{ display: "flex", flexDirection: "column" }}>
                <div
                  style={{
                    width: "100%",
                    background: "#ddd",
                    borderBottom: "1px solid var(--lanzalake-grey)",
                    cursor: "pointer",
                  }}
                  onClick={(_) => {
                    this.setState({
                      ...toggleExpandedCategory(cat, collapse, expanded),
                    })
                  }}
                >
                  {registration.get_category_label(intl, cat)}[
                  {searchString && searchString.length > 0
                    ? `${filteredItems.length}/${items.length}`
                    : items.length}
                  ]
                </div>
                {content}
              </div>
            )
          })}
        </div>
      </div>
    )
  }
}

export const CategorizedSelector = injectIntl(_CategorizedSelector)
